import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Divider, IconButton, Pagination, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import ResolutionContainer from './ResolutionContainer';

const BatchResults = ({
  results,
  page = 1,
  pageSize = 25,
  onPageChange = () => {},
  selectedKeys = [],
  onToggleSelect = () => {},
  canDelete = false,
  filterEntry = null,
}) => {
  const [collapsed, setCollapsed] = useState({});

  if (!results || results.length === 0) {
    return (
      <Typography variant='body1' sx={{ mt: 4 }}>
        No images found for the uploaded entries.
      </Typography>
    );
  }

  const filteredResults = results.filter((entry) => entry.itemNumber || entry.picRef);

  if (filteredResults.length === 0) {
    return (
      <Typography variant='body1' sx={{ mt: 4 }}>
        No valid entries found in the uploaded file.
      </Typography>
    );
  }

  const totalPages = Math.max(1, Math.ceil(filteredResults.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const visibleResults = filteredResults.slice(startIndex, startIndex + pageSize);

  return (
    <Box sx={{ width: '100%', mt: totalPages > 1 ? 2 : 1 }}>
      {totalPages > 1 && (
        <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
          Showing {startIndex + 1}-{Math.min(startIndex + pageSize, filteredResults.length)} of{' '}
          {filteredResults.length} results
        </Typography>
      )}
      {visibleResults.map((entry, index) => {
        const { itemNumber, picRef, error } = entry;
        const key = itemNumber || picRef || index;
        const filteredEntry = filterEntry ? filterEntry(entry) : entry;
        const normalizedImages = filteredEntry?.images
          ? [
              filteredEntry.images.hires || [],
              filteredEntry.images.large || [],
              filteredEntry.images.small || [],
              filteredEntry.images.thumbnail || [],
            ]
          : [];
        const hasImages = normalizedImages.some((variant) => variant.length > 0);
        const title = itemNumber
          ? `Item # ${itemNumber}`
          : picRef
          ? `Picture Reference # ${picRef}`
          : 'Entry';

        return (
          <Box key={key} sx={{ mb: 3 }}>
            <Box
              sx={{
                mb: 2,
                px: 2.5,
                py: 1.2,
                borderRadius: 2,
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(0,0,0,0.04)',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <Typography
                  variant='subtitle2'
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.4,
                    mb: 0,
                    color: 'text.secondary',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {itemNumber ? 'Item #' : 'Picture Reference #'}
                </Typography>
                <Typography
                  variant='h6'
                  sx={{
                    mb: 0,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    color: 'text.primary',
                    lineHeight: 1.2,
                  }}
                >
                  {title.replace(/^(Item #|Picture Reference #)/, '').trim()}
                </Typography>
              </Stack>
              <IconButton
                size='small'
                onClick={() =>
                  setCollapsed((prev) => ({
                    ...prev,
                    [key]: !prev[key],
                  }))
                }
              >
                {collapsed[key] ? <ExpandMoreIcon /> : <ExpandLessIcon />}
              </IconButton>
            </Box>
            {error ? (
              <Typography variant='body2' color='error'>
                {error}
              </Typography>
            ) : hasImages && !collapsed[key] ? (
              <ResolutionContainer
                data={normalizedImages}
                selectedKeys={selectedKeys}
                onToggleSelect={onToggleSelect}
                canDelete={canDelete}
              />
            ) : (
              <Typography variant='body2'>No images found for this entry.</Typography>
            )}
            {index < visibleResults.length - 1 && <Divider sx={{ mt: 3 }} />}
          </Box>
        );
      })}
      {totalPages > 1 && (
        <Stack
          direction='row'
          justifyContent='center'
          sx={{ mt: 2, width: '100%', alignItems: 'center' }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={onPageChange}
            color='primary'
            shape='rounded'
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Box>
  );
};

export default BatchResults;
